// Draws the trader card (src/lib/traderCard.js -> cardContent) on a <canvas>
// as a 1080px-wide portrait image for WhatsApp. Plain Canvas 2D — no new
// dependency. Layout is measured first, then the canvas is sized to fit, so a
// long question never runs off the bottom.

const W = 1080;
const PAD = 72;
const INNER = W - PAD * 2;
const FONT = '"Tajawal","Noto Sans SC","PingFang SC","Microsoft YaHei","Noto Sans Arabic",sans-serif';
const INK = '#1a1814';
const MUTED = '#6f6a62';
const LINE = '#e7e1d6';
const BG = '#FAF8F5';

const isRtl = (s) => /[؀-ۿ]/.test(String(s || ''));
const hasHan = (s) => /[㐀-鿿]/.test(String(s || ''));

// Wrap by words, and by characters inside CJK runs (Chinese has no spaces).
export function wrapLines(ctx, text, maxWidth) {
  const out = [];
  String(text || '').split('\n').forEach((para) => {
    const tokens = hasHan(para) ? para.match(/[㐀-鿿　-〿＀-￯]|[^㐀-鿿　-〿＀-￯\s]+|\s+/g) || [] : para.split(/(\s+)/);
    let line = '';
    tokens.forEach((t) => {
      const next = line + t;
      if (line && ctx.measureText(next).width > maxWidth) {
        out.push(line.trim());
        line = t.trimStart();
      } else {
        line = next;
      }
    });
    if (line.trim() || !out.length) out.push(line.trim());
  });
  return out;
}

export function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';   // keeps the canvas exportable; a host without CORS just fails to load
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Each block: { kind: 'text', text, size, weight, color, gap } | { kind: 'image' } | { kind: 'rule' }.
function blocks(c) {
  const b = [
    { kind: 'logo' },
    { kind: 'text', text: c.headline, size: 54, weight: 700, color: INK, gap: 18 },
    { kind: 'text', text: c.buyerLine, size: 34, weight: 400, color: MUTED, gap: 34 },
    { kind: 'image' },
    { kind: 'text', text: c.product, size: 40, weight: 700, color: INK, gap: 10, max: 3 },
  ];
  if (c.price) b.push({ kind: 'text', text: c.price, size: 34, weight: 400, color: MUTED, gap: 30 });
  if (c.question) {
    b.push({ kind: 'rule' });
    b.push({ kind: 'text', text: '采购商的问题', size: 28, weight: 700, color: MUTED, gap: 10 });
    b.push({ kind: 'text', text: c.question, size: 36, weight: 400, color: INK, gap: c.questionZh ? 16 : 30, max: 8 });
    if (c.questionZh) b.push({ kind: 'text', text: c.questionZh, size: 36, weight: 400, color: MUTED, gap: 30, max: 8 });
  }
  if (c.link) {
    b.push({ kind: 'rule' });
    b.push({ kind: 'text', text: c.cta, size: 32, weight: 400, color: INK, gap: 10 });
    b.push({ kind: 'text', text: c.link, size: 36, weight: 700, color: '#9A7B4F', gap: 14, ltr: true });
    b.push({ kind: 'text', text: c.note, size: 26, weight: 400, color: MUTED, gap: 0 });
  }
  return b;
}

const IMAGE_H = 560;
const LOGO_H = 64;

function layout(ctx, c, { image }) {
  let y = PAD;
  const ops = [];
  blocks(c).forEach((blk) => {
    if (blk.kind === 'logo') { ops.push({ ...blk, y }); y += LOGO_H + 40; return; }
    if (blk.kind === 'image') {
      if (image) { ops.push({ ...blk, y }); y += IMAGE_H + 28; }
      return;
    }
    if (blk.kind === 'rule') { ops.push({ ...blk, y: y + 4 }); y += 34; return; }
    ctx.font = `${blk.weight} ${blk.size}px ${FONT}`;
    let lines = wrapLines(ctx, blk.text, INNER);
    if (blk.max && lines.length > blk.max) lines = [...lines.slice(0, blk.max - 1), `${lines[blk.max - 1]}…`];
    const lh = Math.round(blk.size * 1.45);
    ops.push({ ...blk, y, lines, lh });
    y += lines.length * lh + blk.gap;
  });
  return { ops, height: y + PAD };
}

// Draws onto `canvas` (sized here). logo / image: loaded HTMLImageElements or null.
export function drawTraderCard(canvas, c, { logo = null, image = null } = {}) {
  const ctx = canvas.getContext('2d');
  canvas.width = W;
  const { ops, height } = layout(ctx, c, { image });
  canvas.height = height;   // resizing clears the canvas and resets state

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, height);
  ctx.textBaseline = 'top';

  ops.forEach((op) => {
    if (op.kind === 'logo') {
      if (logo) {
        const w = Math.round((logo.width / logo.height) * LOGO_H);
        ctx.drawImage(logo, PAD, op.y, w, LOGO_H);
      } else {
        ctx.fillStyle = INK; ctx.font = `700 48px ${FONT}`; ctx.direction = 'ltr'; ctx.textAlign = 'left';
        ctx.fillText('MAABAR', PAD, op.y + 6);
      }
      return;
    }
    if (op.kind === 'image') {
      // cover-fit into a rounded box
      const r = 28;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(PAD + r, op.y);
      ctx.arcTo(PAD + INNER, op.y, PAD + INNER, op.y + IMAGE_H, r);
      ctx.arcTo(PAD + INNER, op.y + IMAGE_H, PAD, op.y + IMAGE_H, r);
      ctx.arcTo(PAD, op.y + IMAGE_H, PAD, op.y, r);
      ctx.arcTo(PAD, op.y, PAD + INNER, op.y, r);
      ctx.closePath();
      ctx.clip();
      ctx.fillStyle = '#efe9df';
      ctx.fillRect(PAD, op.y, INNER, IMAGE_H);
      const s = Math.max(INNER / image.width, IMAGE_H / image.height);
      const dw = image.width * s; const dh = image.height * s;
      ctx.drawImage(image, PAD + (INNER - dw) / 2, op.y + (IMAGE_H - dh) / 2, dw, dh);
      ctx.restore();
      return;
    }
    if (op.kind === 'rule') {
      ctx.fillStyle = LINE; ctx.fillRect(PAD, op.y, INNER, 2);
      return;
    }
    ctx.font = `${op.weight} ${op.size}px ${FONT}`;
    ctx.fillStyle = op.color;
    op.lines.forEach((line, i) => {
      const rtl = !op.ltr && isRtl(line);
      ctx.direction = rtl ? 'rtl' : 'ltr';
      ctx.textAlign = rtl ? 'right' : 'left';
      ctx.fillText(line, rtl ? PAD + INNER : PAD, op.y + i * op.lh);
    });
  });
  return canvas;
}

export function canvasToPngBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}
